import { buildSort, goPromise } from "@delanobgt/admin-core/util/helpers";
import { IMeta, IPagination, ISort } from "@delanobgt/admin-core/util/types";
import { CancelTokenSource } from "axios";
import _ from "lodash";
import { EAuthRoles } from "src/core/auth/entities";
import { ApiClientFactory } from "@delanobgt/admin-core/util/types";

import { ERetailWithdrawRequestSortField, IRetailWithdrawRequest, PRetailWithdrawRequestFilter } from "../entities";
import IRetailWithdrawRequestRepository from "../repository";

export default class RetailWithdrawRequestRepository implements IRetailWithdrawRequestRepository<CancelTokenSource> {
  private apiClientFactory: ApiClientFactory;

  // api routes
  private RETAIL_WITHDRAW_REQUEST_ROUTE_FOR_ADMIN = "/admin/retail-withdraw-request";
  private RETAIL_WITHDRAW_REQUEST_ROUTE_FOR_RETAIL = "/retail/retail-withdraw-request";
  private RETAIL_WITHDRAW_REQUEST_ROUTE_BY_ROLE = {
    [EAuthRoles.ADMIN]: this.RETAIL_WITHDRAW_REQUEST_ROUTE_FOR_ADMIN,
    [EAuthRoles.RETAIL]: this.RETAIL_WITHDRAW_REQUEST_ROUTE_FOR_RETAIL,
  };

  constructor(apiClientFactory: ApiClientFactory) {
    this.apiClientFactory = apiClientFactory;
  }

  private buildFilter(filter: PRetailWithdrawRequestFilter) {
    const created_at = filter.created_at_enabled ? `${filter.created_at_start},${filter.created_at_end}` : undefined;

    const clonedFilter = { ...filter };
    delete clonedFilter.created_at_enabled;
    delete clonedFilter.created_at_start;
    delete clonedFilter.created_at_end;

    return {
      ...clonedFilter,
      created_at,
    };
  }

  private getSignalToken(signal: CancelTokenSource) {
    return _.get(signal, "token", null);
  }

  private getError(error: unknown) {
    return _.get(error, "response.data.errors", "Something went wrong!");
  }

  async getRetailWithdrawRequest(
    role: EAuthRoles,
    pagination: IPagination,
    filter: PRetailWithdrawRequestFilter,
    sorts: ISort<ERetailWithdrawRequestSortField>[],
    signal?: CancelTokenSource,
  ): Promise<[IRetailWithdrawRequest[], IMeta]> {
    const sort = buildSort(sorts);
    const params = _.pickBy(
      {
        ...pagination,
        ...this.buildFilter(filter),
      },
      (val) => val,
    );
    const [err, response] = await goPromise(
      this.apiClientFactory({ cancelToken: this.getSignalToken(signal) }).get(
        `${this.RETAIL_WITHDRAW_REQUEST_ROUTE_BY_ROLE[role]}?sort=${sort}`,
        {
          params,
        },
      ),
    );
    if (err) {
      throw this.getError(err);
    }
    const { data, meta } = response.data;
    return [data, meta];
  }

  async getRetailWithdrawRequestById(id: number): Promise<IRetailWithdrawRequest> {
    const [err, response] = await goPromise(
      this.apiClientFactory().get(`${this.RETAIL_WITHDRAW_REQUEST_ROUTE_FOR_ADMIN}/${id}`),
    );
    if (err) {
      throw this.getError(err);
    }
    return response.data;
  }

  async createRetailWithdrawRequest(): Promise<void> {
    const [err] = await goPromise(this.apiClientFactory().post(`${this.RETAIL_WITHDRAW_REQUEST_ROUTE_FOR_RETAIL}`));
    if (err) {
      throw this.getError(err);
    }
  }

  async approveRetailWithdrawRequest(id: number, transaction_code: string): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().post(`${this.RETAIL_WITHDRAW_REQUEST_ROUTE_FOR_ADMIN}/${id}/approve`, {
        transaction_code,
      }),
    );
    if (err) {
      throw this.getError(err);
    }
  }

  async rejectRetailWithdrawRequest(id: number, rejected_reason: string): Promise<void> {
    const [err] = await goPromise(
      this.apiClientFactory().post(`${this.RETAIL_WITHDRAW_REQUEST_ROUTE_FOR_ADMIN}/${id}/reject`, {
        rejected_reason,
      }),
    );
    if (err) {
      throw this.getError(err);
    }
  }
}
